import {memo, useCallback, useEffect, useState} from "react";
import {useSnapshot} from "valtio";
import {Ico} from "./Icons";
import {ContentCatalogFolder} from "@models/contentCatalogFolder.model";
import {Ad} from "@models/Ad.model";
import {useDndContext, useDraggable, useDroppable} from "@dnd-kit/core";
import type {FolderItemDropTarget} from "../contentCatalogDnd";
import * as ContextMenu from "@radix-ui/react-context-menu";
import {contentCatalogStore} from "../contentCatalogStore";
import {
    type CatalogDragItem,
    type CatalogDropTarget,
    type CatalogSection,
    createCatalogDragId,
    createCatalogDropId,
    getFolderItemDropFeedback,
    isCatalogDragItem,
} from "../contentCatalogDnd";
import type { AdMenuItemsFn, FolderMenuItemsFn } from "../contentCatalog";
import { borrowedOwnerName } from "../folderOwner";

interface FolderTreeNodeProps {
    node: ContentCatalogFolder;
    depth: number;
    parentFolderId: number | null;
    section: CatalogSection;
    iconType: "folder" | "collection";
    onSelect: (id: string) => void;
    selectedId: string;
    searchQuery?: string;
    visibleFolderIds?: Set<number>;
    autoExpandFolderIds?: Set<number>;
    visibleAdIdsByFolder?: Map<number, Set<number>>;
    onAdClick?: (ad: Ad) => void;
    adMenuItems?: AdMenuItemsFn;
    folderMenuItems?: FolderMenuItemsFn;
    enableDnd?: boolean;
}

interface TreeAdNodeProps {
    ad: Ad;
    depth: number;
    section: CatalogSection;
    parentFolderId: number;
    isSelected?: boolean;
    onAdClick?: (ad: Ad) => void;
    adMenuItems?: AdMenuItemsFn;
    enableDnd?: boolean;
}

const TreeAdNode = memo(function TreeAdNode({
                        ad,
                        depth,
                        section,
                        parentFolderId,
                        isSelected,
                        onAdClick,
                        adMenuItems,
                        enableDnd,
                    }: TreeAdNodeProps) {
    const adDragData: CatalogDragItem = {
        kind: "ad",
        source: "tree",
        section,
        adId: ad.id,
        parentFolderId,
    };

    const adDropData: CatalogDropTarget = {
        kind: "ad-item",
        section,
        adId: ad.id,
        parentFolderId,
    };

    const {
        attributes,
        listeners,
        setNodeRef: setDragRef,
        transform,
        isDragging,
    } = useDraggable({
        id: createCatalogDragId(adDragData),
        data: adDragData,
        disabled: !enableDnd,
    });

    const {
        setNodeRef: setDropRef,
        isOver,
    } = useDroppable({
        id: createCatalogDropId("tree", adDropData, `ad-item-${section}-${parentFolderId}-${ad.id}`),
        data: adDropData,
        disabled: !enableDnd,
    });

    const setNodeRef = useCallback((element: HTMLElement | null) => {
        setDragRef(element);
        setDropRef(element);
    }, [setDragRef, setDropRef]);

    const style = {
        transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
        paddingLeft: `${20 + (depth + 1) * 14}px`,
    };

    const menuActions = adMenuItems?.(ad, parentFolderId, section);

    const nodeButton = (
        <button
            type="button"
            ref={setNodeRef}
            style={style}
            className={[
                "tree-node",
                "tree-node--ad",
                isSelected ? "tree-node--ad--selected" : "",
                enableDnd ? "cc-draggable" : "",
                isDragging ? "cc-draggable--dragging" : "",
                isOver ? "cc-drop--over" : "",
            ].join(" ").trim()}
            onClick={() => onAdClick?.(ad)}
            {...(enableDnd ? attributes : {})}
            {...(enableDnd ? listeners : {})}
        >
            <span className="tree-node-caret">
                <span style={{width: 12, display: "inline-block"}}/>
            </span>
            <span className="tree-node-icon"><Ico.Ad/></span>
            <span className="tree-node-label">{ad.name || `Ad ${ad.id}`}</span>
        </button>
    );

    if (!menuActions || menuActions.length === 0) {
        return nodeButton;
    }

    return (
        <ContextMenu.Root>
            <ContextMenu.Trigger asChild>{nodeButton}</ContextMenu.Trigger>
            <ContextMenu.Portal>
                <ContextMenu.Content className="radix-context-menu-content" alignOffset={2}>
                    {menuActions.map((action) => (
                        <ContextMenu.Item
                            key={action.label}
                            className={[
                                "radix-context-menu-item",
                                action.danger ? "radix-context-menu-item--danger" : "",
                            ].join(" ").trim()}
                            onSelect={action.onSelect}
                            disabled={action.disabled}
                        >
                            {action.label}
                        </ContextMenu.Item>
                    ))}
                </ContextMenu.Content>
            </ContextMenu.Portal>
        </ContextMenu.Root>
    );
});

const FolderDropFeedback = memo(function FolderDropFeedback({
    folderDropData,
    folderDropId,
}: {
    folderDropData: FolderItemDropTarget;
    folderDropId: string;
}) {
    const {active, over} = useDndContext();

    if (!active || !over || over.id !== folderDropId) return null;

    const dragData = active.data.current;
    if (!isCatalogDragItem(dragData)) return null;

    const initialRect = active.rect.current.initial;
    const translatedRect = active.rect.current.translated;
    const deltaX = translatedRect && initialRect ? translatedRect.left - initialRect.left : 0;
    const deltaY = translatedRect && initialRect ? translatedRect.top - initialRect.top : 0;

    const feedback = getFolderItemDropFeedback(
        {active, over, delta: {x: deltaX, y: deltaY}},
        dragData,
        folderDropData,
    );

    if (feedback === "before") return <span className="tree-drop-line tree-drop-line--before"/>;
    if (feedback === "after") return <span className="tree-drop-line tree-drop-line--after"/>;
    if (feedback === "onto") return <span className="tree-node-drop-onto-indicator"/>;
    return null;
});

export default function FolderTreeNode({
                                           node,
                                           depth = 0,
                                           parentFolderId,
                                           section,
                                           selectedId,
                                           onSelect,
                                           iconType = "folder",
                                           searchQuery,
                                           visibleFolderIds,
                                           autoExpandFolderIds,
                                           visibleAdIdsByFolder,
                                           onAdClick,
                                           adMenuItems,
                                           folderMenuItems,
                                           enableDnd,
                                       }: FolderTreeNodeProps) {
    const { selectedAdIds, organizationId } = useSnapshot(contentCatalogStore);

    const ownerName = borrowedOwnerName(node, organizationId);
    const childFolders = node.children || [];
    const nodeAds = node.ads || [];
    const isSearching = Boolean(searchQuery && searchQuery.trim().length > 0);
    const matchedAdIds = isSearching ? visibleAdIdsByFolder?.get(node.id) : undefined;
    const filteredAds = isSearching ? nodeAds.filter((ad) => matchedAdIds?.has(ad.id)) : nodeAds;
    const hasFolders = childFolders.length > 0;
    const hasAds = filteredAds.length > 0;
    const hasChildren = hasFolders || hasAds;
    const isSelected = selectedId === node.id.toString();
    const [open, setOpen] = useState(false);

    const folderDragData: CatalogDragItem = {
        kind: "folder",
        source: "tree",
        section,
        folderId: node.id,
        parentFolderId,
    };

    const folderDropData: CatalogDropTarget = {
        kind: "folder-item",
        section,
        folderId: node.id,
        parentFolderId,
    };
    const folderDropId = createCatalogDropId("tree", folderDropData, `folder-item-${section}-${node.id}`);

    const isFolderDraggable = section === "all" && enableDnd;

    const {
        attributes,
        listeners,
        setNodeRef: setDragRef,
        transform,
        isDragging,
    } = useDraggable({
        id: createCatalogDragId(folderDragData),
        data: folderDragData,
        disabled: !isFolderDraggable,
    });

    const {
        setNodeRef: setFolderDropRef,
        isOver: isFolderDropOver,
    } = useDroppable({
        id: folderDropId,
        data: folderDropData,
        disabled: !enableDnd,
    });

    const setFolderNodeRef = useCallback((element: HTMLElement | null) => {
        setDragRef(element);
        setFolderDropRef(element);
    }, [setDragRef, setFolderDropRef]);

    const folderContainerDropData: CatalogDropTarget = {
        kind: "folder-container",
        section,
        parentFolderId: node.id,
    };

    const {
        setNodeRef: setFolderContainerDropRef,
        isOver: isFolderContainerOver,
    } = useDroppable({
        id: createCatalogDropId("tree", folderContainerDropData, `folder-list-${section}-${node.id}`),
        data: folderContainerDropData,
        disabled: section !== "all" || !enableDnd,
    });

    const adContainerDropData: CatalogDropTarget = {
        kind: "ad-container",
        section,
        parentFolderId: node.id,
    };

    const {
        setNodeRef: setAdContainerDropRef,
        isOver: isAdContainerOver,
    } = useDroppable({
        id: createCatalogDropId("tree", adContainerDropData, `ad-list-${section}-${node.id}`),
        data: adContainerDropData,
        disabled: !enableDnd,
    });

    const FolderIcon = open && hasChildren ? <Ico.FolderOpen/> : <Ico.FolderClosed/>;
    const CollectionIcon = open && hasChildren ? <Ico.CollectionFilled/> : <Ico.Collection/>;

    const folderButtonStyle = {
        transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
        paddingLeft: `${20 + depth * 14}px`,
    };

    useEffect(() => {
        if (!isSearching) return;
        if (autoExpandFolderIds?.has(node.id)) {
            setOpen(true);
        }
    }, [autoExpandFolderIds, isSearching, node.id]);

    if (visibleFolderIds && !visibleFolderIds.has(node.id)) return null;

    const menuActions = folderMenuItems?.(node, section);

    const folderButton = (
        <button
            type="button"
            ref={setFolderNodeRef}
            className={[
                "tree-node",
                isSelected ? "tree-node--selected" : "",
                iconType === "collection" ? "tree-node--collection" : "",
                isFolderDraggable ? "cc-draggable" : "",
                isDragging ? "cc-draggable--dragging" : "",
                isFolderDropOver ? "cc-drop--over" : "",
            ].join(" ").trim()}
            style={folderButtonStyle}
            onClick={(e) => {
                if (iconType === "folder") {
                    if (e.detail > 1) return;
                    if (hasChildren) setOpen((prev) => !prev);
                    return;
                }

                onSelect(node.id.toString());
                if (hasChildren) setOpen((prev) => !prev);
            }}
            onDoubleClick={() => {
                if (iconType !== "folder") return;
                onSelect(node.id.toString());
            }}
            {...(isFolderDraggable ? attributes : {})}
            {...(isFolderDraggable ? listeners : {})}
            data-folder-node-id={node.id}
        >
            {isFolderDropOver && enableDnd && (
                <FolderDropFeedback folderDropData={folderDropData as FolderItemDropTarget} folderDropId={folderDropId}/>
            )}
            <span className="tree-node-caret">
                {hasChildren
                    ? open ? <Ico.ChevronDown/> : <Ico.ChevronRight/>
                    : <span style={{width: 12, display: "inline-block"}}/>}
            </span>
            <span className="tree-node-icon">
                {iconType === "collection" ? CollectionIcon : FolderIcon}
            </span>
            <span className="tree-node-label">
                <span className="tree-node-name">{node.name}</span>
                {ownerName && <span className="tree-node-owner">{ownerName}</span>}
            </span>
        </button>
    );

    const skipFolderMenu = !menuActions || menuActions.length === 0;

    return (
        <div>
            {skipFolderMenu ? (
                folderButton
            ) : (
                <ContextMenu.Root>
                    <ContextMenu.Trigger asChild>{folderButton}</ContextMenu.Trigger>
                    <ContextMenu.Portal>
                        <ContextMenu.Content className="radix-context-menu-content" alignOffset={2}>
                            {menuActions.map((action) => (
                                <ContextMenu.Item
                                    key={action.label}
                                    className={[
                                        "radix-context-menu-item",
                                        action.danger ? "radix-context-menu-item--danger" : "",
                                    ].join(" ").trim()}
                                    onSelect={action.onSelect}
                                    disabled={action.disabled}
                                >
                                    {action.label}
                                </ContextMenu.Item>
                            ))}
                        </ContextMenu.Content>
                    </ContextMenu.Portal>
                </ContextMenu.Root>
            )}

            {open && hasChildren && (
                <div className="tree-node-children">
                    {hasFolders && (
                        <div
                            ref={setFolderContainerDropRef}
                            className={`tree-node-children tree-node-children--folders cc-dropzone${isFolderContainerOver ? " cc-dropzone--over" : ""}`}
                        >
                            {childFolders.map((child) => (
                                <FolderTreeNode
                                    key={child.id}
                                    node={child}
                                    depth={depth + 1}
                                    parentFolderId={node.id}
                                    section={section}
                                    selectedId={selectedId}
                                    onSelect={onSelect}
                                    iconType={iconType}
                                    searchQuery={searchQuery}
                                    visibleFolderIds={visibleFolderIds}
                                    autoExpandFolderIds={autoExpandFolderIds}
                                    visibleAdIdsByFolder={visibleAdIdsByFolder}
                                    onAdClick={onAdClick}
                                    adMenuItems={adMenuItems}
                                    folderMenuItems={folderMenuItems}
                                    enableDnd={enableDnd}
                                />
                            ))}
                        </div>
                    )}

                    {hasAds && (
                        <div
                            ref={setAdContainerDropRef}
                            className={`tree-node-children tree-node-children--ads cc-dropzone${isAdContainerOver ? " cc-dropzone--over" : ""}`}
                        >
                            {filteredAds.map((ad) => (
                                <TreeAdNode
                                    key={`ad-${section}-${node.id}-${ad.id}`}
                                    ad={ad}
                                    depth={depth}
                                    section={section}
                                    parentFolderId={node.id}
                                    isSelected={selectedAdIds.includes(ad.id)}
                                    onAdClick={onAdClick}
                                    adMenuItems={adMenuItems}
                                    enableDnd={enableDnd}
                                />
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
